import { Box, CircularProgress, Grid, Skeleton, useTheme } from '@mui/material';
import React from 'react';
import { ITheme } from 'types/theme';
import { IProps } from 'types/common';

interface ILoaderProps extends IProps {
	isLoading: boolean;
	circularLoaderSize?: number;
	variant?:
		| 'profile'
		| 'settings'
		| 'widget'
		| 'header'
		| 'circular'
		| 'text'
		| 'rectangular';
}

const Loader: React.FC<ILoaderProps> = ({
	isLoading,
	children,
	variant,
	circularLoaderSize
}) => {
	const theme = useTheme<ITheme>();
	if (isLoading) {
		switch (variant) {
			case 'settings':
				return (
					<Grid
						container
						spacing={5}
						m={1}
						flexDirection="row"
						data-testid="loader-settings"
					>
						<Grid item xs={6}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={12}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
						<Grid item xs={10}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={12}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
						<Grid item xs={6}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={10}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={8}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
						<Grid item xs={10}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={8}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
						<Grid item xs={6} />
						<Grid item xs={8}>
							<Box display="flex" flexDirection="row">
								<Skeleton
									variant="rectangular"
									height={40}
									width={120}
									sx={{ marginRight: 3 }}
								/>
								<Skeleton variant="rectangular" height={40} width={120} />
							</Box>
						</Grid>
					</Grid>
				);
			case 'profile':
				return (
					<Grid
						container
						spacing={5}
						flexDirection="column"
						data-testid="loader-profile"
					>
						<Grid item xs={4}>
							<Skeleton variant="rectangular" height="160px" />
						</Grid>
						<Grid item container xs={12} flexDirection="row" width="100%">
							<Grid item mr={5}>
								<Skeleton variant="rectangular" width={300} height="50px" />
							</Grid>
							<Grid item>
								<Skeleton variant="rectangular" width={300} height="50px" />
							</Grid>
						</Grid>
						<Grid item container xs={12} flexDirection="row" width="100%">
							<Grid item mr={5}>
								<Skeleton variant="rectangular" width={300} height="50px" />
							</Grid>
							<Grid item>
								<Skeleton variant="rectangular" width={300} height="50px" />
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Skeleton variant="text" />
						</Grid>
						<Grid item xs={12}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
						<Grid item xs={12}>
							<Skeleton variant="rectangular" height="50px" />
						</Grid>
					</Grid>
				);
			case 'header':
				return (
					<Skeleton variant="text" width={130} data-testid="loader-header" />
				);
			case 'widget':
				return (
					<Skeleton
						variant="rectangular"
						height={200}
						sx={{ m: 4 }}
						data-testid="loader-widget"
					/>
				);
			case 'circular':
				return (
					<>
						<svg height={0}>
							<defs>
								<linearGradient id="loader_gradient">
									<stop offset="0%" stopColor={theme.palette.brand.first} />
									<stop offset="50%" stopColor={theme.palette.brand.second} />
									<stop offset="100%" stopColor={theme.palette.brand.third} />
								</linearGradient>
							</defs>
						</svg>
						<CircularProgress
							size={circularLoaderSize}
							thickness={4}
							sx={{ 'svg circle': { stroke: 'url(#loader_gradient)' } }}
						/>
					</>
				);
			default:
				return <Skeleton variant={variant} data-testid="loader-default" />;
		}
	}
	return children || null;
};

Loader.defaultProps = {
	variant: 'text',
	circularLoaderSize: 80
};

export default Loader;
